/**
 * ProjectWorkOrderDetailPage - Detail view for a single Project WO.
 *
 * Shows project metadata, a running hour-pool readout (total /
 * allocated / remaining), and the list of child work orders with an
 * inline editor for allocated_hours. Edits are buffered locally and
 * submitted as a single bulk PATCH so the sum is validated in one
 * round-trip — a partial save would risk leaving the project in an
 * over-allocated state.
 *
 * @route /project-work-orders/:id
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { projectWorkOrderService } from '../../services/projectWorkOrderService';
import type { ProjectWorkOrder, ProjectChildWorkOrder } from '../../services/projectWorkOrderService';
import { camelizeKeys } from '../../services/api';
import { qk } from '../../lib/queryKeys';
import { Spinner } from '../../components/atoms/Spinner';
import { Button, BackLink } from '../../components/atoms';
import { DetailActionBar, ProjectHoursSummary } from '../../components/molecules';
import { generateProjectWorkOrderPdf } from '../../utils/pdfGenerator';
import { formatLabel } from '../../utils/formatLabel';
import { formatDay } from '../../utils/formatDate';
import { PageContainer } from '../../components/templates/PageContainer';

/** Display-only status badge palette (mirrors the list page). */
const STATUS_BADGE_COLORS: Record<string, string> = {
	draft: 'bg-gray-100 text-gray-700 border border-gray-300',
	active: 'bg-blue-100 text-blue-700 border border-blue-300',
	completed: 'bg-green-100 text-green-700 border border-green-300',
	cancelled: 'bg-gray-200 text-gray-500 border border-gray-300',
};

const ProjectWorkOrderDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	/**
	 * Local edit buffer for allocated_hours per child WO.
	 * Keyed by child WO id; values are stringified numbers so empty
	 * input doesn't get coerced to 0 mid-typing. We hydrate from the
	 * loaded project and re-hydrate after each successful save.
	 */
	const [allocDraft, setAllocDraft] = useState<Record<number, string>>({});
	const [saveMessage, setSaveMessage] = useState<string | null>(null);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	const query = useQuery({
		queryKey: qk.projectWorkOrders.detail(id ?? ''),
		queryFn: async () => {
			const res = await projectWorkOrderService.getById(id as string);
			const rawData = (res.data as any)?.data ?? res.data;
			return camelizeKeys(rawData) as ProjectWorkOrder;
		},
		enabled: !!id,
	});

	const project: ProjectWorkOrder | null = query.data ?? null;
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	/* Seed the draft from the server's current values so the editor starts
	 * in a "no pending changes" state. Re-runs whenever the query data
	 * changes (initial load and after a save invalidates + refetches). */
	useEffect(() => {
		if (!project) return;
		const next: Record<number, string> = {};
		(project.workOrders ?? []).forEach((wo) => {
			next[wo.id] = wo.allocatedHours == null ? '' : String(wo.allocatedHours);
		});
		setAllocDraft(next);
	}, [project]);

	/** Live total of the draft values — drives the "X / Y hrs" readout. */
	const draftTotal = useMemo(() => {
		return Object.values(allocDraft).reduce((sum, raw) => {
			const n = Number(raw);
			return sum + (Number.isFinite(n) ? n : 0);
		}, 0);
	}, [allocDraft]);

	const totalHours = Number(project?.totalHours ?? 0);
	const remaining = totalHours - draftTotal;
	const overAllocated = remaining < -0.0001; // small epsilon to absorb FP noise

	/* Whether any child has closed out. The over/under readout compares actual
	   hours against estimates on closed work, so it stays hidden until there is
	   closed work to compare. */
	const hasClosedWork = useMemo(
		() => (project?.workOrders ?? []).some((wo) => wo.status === 'completed' || wo.status === 'cancelled'),
		[project],
	);

	/** Whether any draft value differs from the server's value. */
	const hasPendingChanges = useMemo(() => {
		if (!project?.workOrders) return false;
		for (const wo of project.workOrders) {
			const draft = allocDraft[wo.id] ?? '';
			const server = wo.allocatedHours == null ? '' : String(wo.allocatedHours);
			if (draft !== server) return true;
		}
		return false;
	}, [allocDraft, project]);

	const saveAllocationsMutation = useMutation({
		mutationFn: (allocations: { workOrderId: number; hours: number }[]) =>
			projectWorkOrderService.setAllocations(id as string, allocations),
		onSuccess: () => {
			/* Refetch this project (re-seeds the draft via the effect above)
			 * and refresh the list so allocation totals stay in sync. */
			queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.all });
			if (id) queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.detail(id) });
			setSaveMessage('Saved.');
		},
	});
	const isSaving = saveAllocationsMutation.isPending;

	const handleSaveAllocations = async () => {
		if (!id || !project) return;
		setSaveMessage(null);
		if (overAllocated) {
			setSaveMessage('Cannot save — allocations exceed the project total.');
			return;
		}
		const allocations = (project.workOrders ?? []).map((wo) => ({
			workOrderId: wo.id,
			hours: Number(allocDraft[wo.id] || 0),
		}));
		try {
			await saveAllocationsMutation.mutateAsync(allocations);
		} catch (err) {
			setSaveMessage(err instanceof Error ? err.message : 'Failed to save allocations');
		}
	};

	/**
	 * Renders a single PDF report for the project using jspdf, then
	 * triggers a download. All content comes from the already-loaded
	 * `project` state, so no extra network round-trip is needed.
	 */
	const handleGeneratePdf = () => {
		if (!project) return;
		setIsGeneratingPdf(true);
		try {
			generateProjectWorkOrderPdf(project);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to generate PDF');
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	const deleteMutation = useMutation({
		mutationFn: () => projectWorkOrderService.delete(id as string),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.all });
			navigate('/project-work-orders');
		},
	});

	const handleDelete = async () => {
		if (!id) return;
		if (!window.confirm('Delete this project? Child work orders will keep their hour allocations but become standalone.')) return;
		try {
			await deleteMutation.mutateAsync();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete project');
		}
	};

	if (isLoading) {
		return (
			<div className="p-24px flex items-center justify-center min-h-[400px]">
				<Spinner size="lg" label="Loading project" />
			</div>
		);
	}

	if (error || !project) {
		return (
			<div className="p-24px">
				<div className="bg-red-50 border border-red-200 rounded-lg p-24px text-center">
					<p className="text-14px font-sans text-red-700">{error || 'Project not found'}</p>
					<button type="button" onClick={() => navigate('/project-work-orders')} className="mt-12px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">
						Back to Projects
					</button>
				</div>
			</div>
		);
	}

	const children: ProjectChildWorkOrder[] = project.workOrders ?? [];

	return (
		<PageContainer block="project-work-order-detail-page" dataRef="page-project-work-order-detail-page">
			<BackLink to="/project-work-orders" label="Back to Projects" />

			{/* --- Header ---
			    Stacked until desktop, matching the work order / inspection
			    detail pages. Going side-by-side at `tablet` crammed the project
			    name against the action cluster in the 768–1024px zone; staying
			    stacked there gives the actions a full-width row of their own. */}
			<div className="flex flex-col desktop:flex-row desktop:items-start desktop:justify-between gap-12px mb-24px">
				<div>
					<div className="flex items-center gap-12px">
						<h1 className="page-title text-24px font-heading text-brand-heading m-0">{project.name}</h1>
						<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide', STATUS_BADGE_COLORS[project.status] || STATUS_BADGE_COLORS.draft].join(' ')}>
							{project.status}
						</span>
					</div>
					{project.description && (
						<p className="text-14px text-brand-text-muted mt-4px max-w-[640px]">{project.description}</p>
					)}
				</div>
				{/* Same responsive action cluster the work order / inspection detail
				    pages use: Edit is the dominant action, PDF is a neutral
				    utility beside it, and Delete moves into the overflow kebab —
				    it's destructive and shouldn't be a full-size button one
				    mis-tap away from Edit on a phone. */}
				<DetailActionBar
					primaryAction={{ label: 'Edit', onClick: () => navigate(`/project-work-orders/${project.id}/edit`) }}
					actions={[
						{ key: 'pdf', label: isGeneratingPdf ? 'Generating…' : 'Generate PDF', onClick: handleGeneratePdf, variant: 'quiet', isLoading: isGeneratingPdf },
					]}
					menuItems={[{ label: 'Delete', onClick: handleDelete, variant: 'danger' }]}
					menuAriaLabel={`Actions for project ${project.name}`}
				/>
			</div>

			{/* --- Inherited fields readout --- */}
			{/* Surface the building (and its address) the project anchors to.
			    Child WOs created via "+ Add Work Order" inherit both fields
			    from this row, so showing them here makes the inheritance
			    chain explicit. The company itself appears via its child
			    WOs and the page-scoping route, so we lead with the building. */}
			{project.location && (
				<div className="bg-white border border-brand-border rounded-md p-20px mb-24px">
					<div className="text-12px uppercase text-brand-text-muted tracking-wide">Building</div>
					<div className="text-16px font-semibold text-brand-text mt-2px">{project.location.name}</div>
					<div className="text-12px text-brand-text-muted mt-2px">
						{[project.location.address, project.location.city, project.location.state, project.location.zip]
							.filter((v) => v && String(v).trim() !== '')
							.join(', ')}
					</div>
					<p className="text-12px text-brand-text-muted mt-8px">
						Work orders added to this project automatically inherit this building and its address.
					</p>
				</div>
			)}

			{/* --- Business Line + Billing + Frequency readout --- */}
			{/* These project-level fields apply uniformly to every child WO
			    (the child create form no longer surfaces a Billing section,
			    and Settings on the child is slimmed down to per-WO toggles).
			    Render the card only when at least one project-level value
			    is set so the page stays tidy for minimally-configured
			    projects. */}
			{((project.billingInfo && project.billingInfo.length > 0) || project.billingNotes || project.frequency || project.businessLine) && (
				<div className="bg-white border border-brand-border rounded-md p-20px mb-24px">
					<div className="grid grid-cols-1 tablet:grid-cols-3 gap-16px">
						{project.businessLine && (
							<div>
								<div className="text-12px uppercase text-brand-text-muted tracking-wide">Business Line</div>
								<div className="text-14px text-brand-text mt-4px">{formatLabel(project.businessLine)}</div>
							</div>
						)}
						{project.billingInfo && project.billingInfo.length > 0 && (
							<div>
								<div className="text-12px uppercase text-brand-text-muted tracking-wide">Billing</div>
								<div className="flex flex-wrap gap-4px mt-4px">
									{project.billingInfo.map((tag) => (
										<span key={tag} className="px-8px py-2px rounded-full text-12px font-medium bg-brand-surface text-brand-text border border-brand-border">{tag}</span>
									))}
								</div>
							</div>
						)}
						{project.frequency && (
							<div>
								<div className="text-12px uppercase text-brand-text-muted tracking-wide">Frequency</div>
								<div className="text-14px text-brand-text mt-4px">{project.frequency}</div>
							</div>
						)}
					</div>
					{project.billingNotes && (
						<div className="mt-12px">
							<div className="text-12px uppercase text-brand-text-muted tracking-wide">Billing Notes</div>
							<p className="text-13px text-brand-text mt-4px whitespace-pre-wrap">{project.billingNotes}</p>
						</div>
					)}
				</div>
			)}

			{/* --- Hour pool summary ---
				Estimated (open) vs actual: the pool is only charged once a work
				order closes out with its real hours, so open estimates show as
				earmarked rather than spent. The shared ProjectHoursSummary
				molecule renders the same numbers here, on the project list, and
				on the work order create panel. */}
			<div className="project-work-order-detail__hours bg-white border border-brand-border rounded-md p-20px mb-24px">
				<h2 className="text-16px font-semibold text-brand-heading mb-12px">Project Hours</h2>
				<ProjectHoursSummary
					totalHours={totalHours}
					estimatedOpen={Number(project.hoursEstimatedOpen ?? 0)}
					actualUsed={Number(project.hoursActualUsed ?? 0)}
					remaining={Number(project.hoursRemaining ?? totalHours)}
					projectedRemaining={Number(project.hoursProjectedRemaining ?? totalHours)}
					variance={Number(project.hoursVariance ?? 0)}
					hasClosedWork={hasClosedWork}
				/>

				{/* Live allocation draft readout — this block tracks the
				    estimates being edited in the rows below, which is a
				    different question from what the pool has been charged. */}
				<div className="project-work-order-detail__allocation-draft mt-16px pt-16px border-t border-brand-border">
					<div className="text-12px uppercase text-brand-text-muted tracking-wide">Estimates committed (all work orders)</div>
					<div className={['text-20px font-heading', overAllocated ? 'text-status-error' : 'text-brand-text'].join(' ')}>
						{draftTotal} / {totalHours} hrs
					</div>
					{overAllocated && (
						<p className="text-12px text-status-error mt-8px">Allocations exceed the project total. Reduce some rows or increase the project total before saving.</p>
					)}
				</div>
			</div>

			{/* --- Child work orders --- */}
			<div className="flex items-center justify-between mb-12px">
				<h2 className="text-16px font-semibold text-brand-text">Work Orders ({children.length})</h2>
				<Button variant="secondary" onClick={() => navigate(`/work-orders/create?project_work_order_id=${project.id}`)}>+ Add Work Order</Button>
			</div>

			{children.length === 0 ? (
				<div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md bg-white">
					No work orders yet. Click "+ Add Work Order" to attach one to this project.
				</div>
			) : (
				<div className="overflow-x-auto border border-brand-border rounded-md bg-white">
					<table className="w-full text-14px">
						<thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
							<tr>
								<th className="text-left px-16px py-12px font-medium">Work Order</th>
								<th className="text-left px-16px py-12px font-medium">Assignee</th>
								<th className="text-left px-16px py-12px font-medium">Status</th>
								<th className="text-left px-16px py-12px font-medium">Scheduled</th>
								<th className="text-left px-16px py-12px font-medium">Estimated Hours</th>
								{/* Actual + over/under only carry meaning once a work
								    order has closed out with its real hours. */}
								<th className="text-left px-16px py-12px font-medium">Actual</th>
								<th className="text-left px-16px py-12px font-medium">Over / Under</th>
							</tr>
						</thead>
						<tbody>
							{children.map((wo) => {
								const assigneeName = wo.assignee
									? `${wo.assignee.firstName ?? ''} ${wo.assignee.lastName ?? ''}`.trim()
									: '—';
								/* A closed work order's actual hours are final, so its
								   variance against the estimate is what the pool was
								   really charged. Open rows show a dash instead. */
								const isClosed = wo.status === 'completed' || wo.status === 'cancelled';
								const actualHours = Number(wo.totalHours ?? 0);
								const estimatedHours = Number(wo.allocatedHours ?? 0);
								const variance = Number((actualHours - estimatedHours).toFixed(2));
								return (
									<tr key={wo.id} className="border-t border-brand-border hover:bg-brand-surface/40">
										<td className="px-16px py-12px">
											<button type="button" onClick={() => navigate(`/work-orders/${wo.id}`)} className="text-14px font-semibold text-brand-accent hover:underline text-left">
												{wo.projectName}
											</button>
										</td>
										<td className="px-16px py-12px text-12px text-brand-text">{assigneeName}</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted">{wo.status}</td>
										{/* scheduledDate is date-only (UTC midnight) — re-parse at local
										    midnight so the calendar date doesn't roll back a day. */}
										<td className="px-16px py-12px text-12px text-brand-text-muted">{formatDay(wo.scheduledDate as string | null)}</td>
										<td className="px-16px py-12px">
											<input
												type="number"
												min={0}
												step={0.25}
												value={allocDraft[wo.id] ?? ''}
												onChange={(e) => setAllocDraft((prev) => ({ ...prev, [wo.id]: e.target.value }))}
												className="w-[100px] px-8px py-4px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
												placeholder="0"
											/>
										</td>
										<td className="px-16px py-12px text-12px text-brand-text">
											{isClosed ? `${actualHours} hrs` : '—'}
										</td>
										<td className="px-16px py-12px text-12px">
											{isClosed ? (
												<span className={[
													'font-semibold',
													variance > 0 ? 'text-status-error' : variance < 0 ? 'text-status-success-dark' : 'text-brand-text-muted',
												].join(' ')}>
													{variance > 0 ? `+${variance}` : variance < 0 ? `−${Math.abs(variance)}` : '0'} hrs
												</span>
											) : (
												<span className="text-brand-text-subtle">Not closed</span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{children.length > 0 && (
				<div className="project-work-order-detail-page__actions flex flex-wrap items-center justify-end gap-8px mt-16px">
					{saveMessage && <span className="text-12px text-brand-text-muted">{saveMessage}</span>}
					<Button variant="primary" onClick={handleSaveAllocations} disabled={isSaving || !hasPendingChanges || overAllocated}>
						{isSaving ? 'Saving...' : 'Save Allocations'}
					</Button>
				</div>
			)}
		</PageContainer>
	);
};

export default ProjectWorkOrderDetailPage;
